import React from 'react'
import moment from 'jalali-moment'
import HttpServices from '../../utils/Http.services'
import { checkTranslation } from '../../utils/useful'
import { fakeFormsData } from '../../variables/fakeData'
import { processStages } from '../../variables/userWidgets'
import { getUserWidgetPreviewRecords } from '../../utils/processWidgetData'
import {
    canViewProcessStage,
    getRecordProcessStageKey,
} from '../../utils/processStageAccess'
import { buildProcessWidgetRequest } from '../../utils/widgetRequests'
import ProfessionalStatusInput from '../inputs/ProfessionalStatusInput'
import './HorseBreederContractsHome.css'

const contractsWidget = {
    fetchUrl: 'getUserRecords',
    frame: 'forms',
    page: 'forms',
    self: 'customer',
    limit: 1000,
    sort: { uDate: -1 },
    postBody: {
        frame: 'forms',
        'values.lastFollowUpResult': 'قرارداد',
    },
    populates: [
        { key: 'values.modelMale', model: 'records', select: ['values.name', 'values.faName'] },
        { key: 'values.veterinarian', model: 'employees', select: ['name'] },
        { key: 'values.customer', model: 'employees', select: ['name'] },
    ],
}

const getRelationshipName = (value, fallback = '—') => {
    if (!value) return fallback
    if (typeof value === 'string' || typeof value === 'number') return String(value)

    return value.name
        || value.values?.faName
        || value.values?.name
        || value.title
        || fallback
}

const formatDate = value => {
    if (!value) return '—'
    return moment(value).locale('fa').format('jYYYY/jMM/jDD')
}

class HorseBreederContractsHome extends React.Component {
    state = {
        contracts: [],
        error: null,
        isLoading: true,
    }

    componentDidMount() {
        this.fetchContracts()
        window.addEventListener('reloadFetchData', this.fetchContracts)
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.useFakeData !== this.props.useFakeData
            || prevProps.user?.info?._id !== this.props.user?.info?._id
        ) {
            this.fetchContracts()
        }
    }

    componentWillUnmount() {
        window.removeEventListener('reloadFetchData', this.fetchContracts)
    }

    fetchContracts = () => {
        const userId = this.props.user?.info?._id
        const body = buildProcessWidgetRequest({
            widget: contractsWidget,
            userId,
            defaultFilter: {
                'values.lastFollowUpResult': 'قرارداد',
            },
        })

        this.setState({ error: null, isLoading: true })

        if (this.props.useFakeData) {
            const previewContracts = getUserWidgetPreviewRecords({
                records: fakeFormsData.filter(record => (
                    record.values?.lastFollowUpResult === 'قرارداد'
                )),
                userType: 'horseBreeder',
                userId,
                limit: 6,
            })
            this.setState({ contracts: previewContracts, isLoading: false })
            return
        }

        HttpServices.request(contractsWidget.fetchUrl, body, (result, error) => {
            if (error) {
                this.setState({ contracts: [], error, isLoading: false })
                return
            }

            this.setState({
                contracts: Array.isArray(result?.info) ? result.info : [],
                isLoading: false,
            })
        })
    }

    getCurrentStageLabel = stageKey => {
        const stage = processStages[stageKey]
        if (!stageKey || !stage) return checkTranslation('{{lang}}Not started')
        if (!canViewProcessStage(stage, 'horseBreeder')) {
            return checkTranslation('{{lang}}Restricted current stage')
        }
        return checkTranslation(stage.name)
    }

    renderContract = (contract, index) => {
        const values = contract.values || {}
        const currentStageKey = getRecordProcessStageKey(contract)
        const formId = values.formId || contract._id || `${index + 1}`
        const mareName = values.mareName || values.model || '—'
        const stallionName = values.stallionName
            || getRelationshipName(values.modelMale)
        const veterinarianName = getRelationshipName(values.veterinarian)
        const roadmapTitleId = `contract-roadmap-${String(contract._id || index).replace(/[^a-zA-Z0-9_-]/g, '-')}`

        return (
            <article className="breeder-contract" key={contract._id || formId}>
                <header className="breeder-contract__header">
                    <div className="breeder-contract__identity">
                        <span className="breeder-contract__index" aria-hidden="true">
                            {String(index + 1).padStart(2, '0')}
                        </span>
                        <div>
                            <p>{checkTranslation('{{lang}}Contract')}</p>
                            <h2>{formId}</h2>
                        </div>
                    </div>
                    <div className="breeder-contract__status">
                        <span>{checkTranslation('{{lang}}Current Status')}</span>
                        <strong>{this.getCurrentStageLabel(currentStageKey)}</strong>
                    </div>
                </header>

                <dl className="breeder-contract__facts">
                    <div>
                        <dt>{checkTranslation('{{lang}}Mare Name')}</dt>
                        <dd>{getRelationshipName(mareName)}</dd>
                    </div>
                    <div>
                        <dt>{checkTranslation('{{lang}}Stallion Name')}</dt>
                        <dd>{stallionName}</dd>
                    </div>
                    <div>
                        <dt>{checkTranslation('{{lang}}Veterinarian')}</dt>
                        <dd>{veterinarianName}</dd>
                    </div>
                    <div>
                        <dt>{checkTranslation('{{lang}}Last Update Date')}</dt>
                        <dd>{formatDate(contract.uDate)}</dd>
                    </div>
                </dl>

                <ProfessionalStatusInput
                    data={{
                        ...(values.currentStatus && typeof values.currentStatus === 'object'
                            ? values.currentStatus
                            : {}),
                        key: currentStageKey,
                        value: typeof values.currentStatus === 'string'
                            ? values.currentStatus
                            : values.currentStatus?.value || currentStageKey,
                    }}
                    viewerUserType="horseBreeder"
                    inlineRoadmap={true}
                    roadmapTitleId={roadmapTitleId}
                    fullValues={contract}
                    header={{ key: 'values.currentStatus', information: {} }}
                />
            </article>
        )
    }

    render() {
        const { contracts, error, isLoading } = this.state

        return (
            <main className="breeder-contracts" dir="rtl">
                <header className="breeder-contracts__intro">
                    <div>
                        <p className="breeder-contracts__eyebrow">
                            {checkTranslation('{{lang}}My Contracts')}
                        </p>
                        <h1>{checkTranslation('{{lang}}Your contracts, step by step')}</h1>
                        <p className="breeder-contracts__description">
                            {checkTranslation('{{lang}}Follow the current position of every contract in one place')}
                        </p>
                    </div>
                    {!isLoading && !error && (
                        <div className="breeder-contracts__count" aria-label={`${contracts.length} ${checkTranslation('{{lang}}Contracts')}`}>
                            <strong>{contracts.length.toLocaleString('fa-IR')}</strong>
                            <span>{checkTranslation('{{lang}}Active contracts')}</span>
                        </div>
                    )}
                </header>

                {isLoading && (
                    <div className="breeder-contracts__state" role="status">
                        <span className="breeder-contracts__loader" aria-hidden="true" />
                        <p>{checkTranslation('{{lang}}Loading contracts')}</p>
                    </div>
                )}

                {!isLoading && error && (
                    <div className="breeder-contracts__state breeder-contracts__state--error" role="alert">
                        <p>{checkTranslation('{{lang}}Unable to load contracts')}</p>
                    </div>
                )}

                {!isLoading && !error && contracts.length === 0 && (
                    <div className="breeder-contracts__state">
                        <p>{checkTranslation('{{lang}}No contracts found')}</p>
                        <span>{checkTranslation('{{lang}}Your contracts will appear here when they are created')}</span>
                    </div>
                )}

                {!isLoading && !error && contracts.length > 0 && (
                    <section className="breeder-contracts__list" aria-label={checkTranslation('{{lang}}My Contracts')}>
                        {contracts.map(this.renderContract)}
                    </section>
                )}
            </main>
        )
    }
}

export default HorseBreederContractsHome
